import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/Layout";
import LegacyRedirect from "./components/LegacyRedirect";
import AnalyticsTracker from "./components/AnalyticsTracker";
import StagingPublishBar from "./components/publish/StagingPublishBar";
import DevPublishBar from "./components/dev/PublishBar";
const Home = lazy(() => import("./pages/Home"));

const ConstructionSiteCameras = lazy(() => import("./pages/ConstructionSiteCameras"));
const RemoteMonitoringCameras = lazy(() => import("./pages/RemoteMonitoringCameras"));
const TimeLapseCameras = lazy(() => import("./pages/TimeLapseCameras"));
const TemporarySiteSecurityCameras = lazy(() => import("./pages/TemporarySiteSecurityCameras"));
const SolarSiteCameras = lazy(() => import("./pages/SolarSiteCameras"));
const BuildingSiteCCTV = lazy(() => import("./pages/BuildingSiteCCTV"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const Locations = lazy(() => import("./pages/Locations"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyPremierBuild = lazy(() => import("./pages/CaseStudyPremierBuild"));
const CaseStudyCoatesHire = lazy(() => import("./pages/CaseStudyCoatesHire"));
const CaseStudyAcciona = lazy(() => import("./pages/CaseStudyAcciona"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CloudStorage = lazy(() => import("./pages/CloudStorage"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const WatchHowJobcamProtects = lazy(() => import("./pages/WatchHowJobcamProtects"));
const Councils = lazy(() => import("./pages/Councils"));
const CivilWorks = lazy(() => import("./pages/CivilWorks"));
const Support = lazy(() => import("./pages/Support"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <StagingPublishBar />
      {import.meta.env.DEV && <DevPublishBar />}
      <BrowserRouter>
        <AnalyticsTracker />
        <Layout>
          <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/job-site-cameras" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-security-cameras" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-security-cameras/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-cameras-nsw" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras-nsw/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-cameras-vic" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras-vic/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-cameras-qld" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras-qld/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-cameras-wa" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras-wa/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-cameras-sa" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras-sa/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-cameras-act" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras-act/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-cameras-tas" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras-tas/:slug" element={<LegacyRedirect />} />
              <Route path="/job-site-cameras-nt" element={<Navigate to="/locations" replace />} />
              <Route path="/job-site-cameras-nt/:slug" element={<LegacyRedirect />} />
              <Route path="/construction-site-cameras" element={<ConstructionSiteCameras />} />
              <Route path="/remote-monitoring-cameras" element={<RemoteMonitoringCameras />} />
              <Route path="/construction-time-lapse-cameras" element={<TimeLapseCameras />} />
              <Route path="/temporary-site-security-cameras" element={<TemporarySiteSecurityCameras />} />
              <Route path="/solar-site-cameras" element={<SolarSiteCameras />} />
              <Route path="/building-site-cctv" element={<BuildingSiteCCTV />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/locations/:city" element={<LocationPage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/case-studies/premier-build" element={<CaseStudyPremierBuild />} />
              <Route path="/case-studies/coates-hire" element={<CaseStudyCoatesHire />} />
              <Route path="/case-studies/acciona" element={<CaseStudyAcciona />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />
              <Route path="/cloud-storage" element={<CloudStorage />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/councils" element={<Councils />} />
              <Route path="/civil-works" element={<CivilWorks />} />
              <Route path="/support" element={<Support />} />
              <Route path="/watch-how-jobcam-protects" element={<WatchHowJobcamProtects />} />
              <Route path="/watch/how-jobcam-protects-your-site" element={<Navigate to="/watch-how-jobcam-protects" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Layout>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
